<template>
    <div class="sc__analysis__tab__box tab-content">
        <div class="sc__analysis__head__box">
            <div class="title__box">
                <div class="head__title__group">
                    <h3 class="title">Syllable Tracker</h3>
                </div>
                <div class="print__data__btn">
                    <WorkpacketPdf :button-label="`Download`" :assessmentId="assessmentId"
                        :organizationId="organizationId" :learnerId="learnerId"
                        v-if="assessmentId && organizationId && learnerId" />
                </div>
            </div>
            <div class="head__label__box__wrapper main__head__info__wrapper">
                <div class="head__information__wrapper">
                    <p>Purpose: The Syllable Tracker helps identify syllables that are “available” as speech
                        targets.</p>
                    <p>Info sources: EESAPP test items, informal observations of vocalizations.</p>
                </div>
                <div class="head__information__wrapper">
                    <h6 class="info__title">Directions</h6>
                    <ul>
                        <li>Put an x in the boxes of all syllables the learner can say.
                            <div class="inner__subpoints">
                                <ul>
                                    <li>Find interactions of vowels (V) and consonants (C).</li>
                                    <li>Mark X in shaded top row if syllable is a single vowel only.</li>
                                </ul>
                            </div>
                        </li>
                        <li>Syllable Tracker information will be used in TASK 5.</li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="information__indicator__wrapper">
            <div class="indicator__checkbox automated">
                <input type="checkbox" id="automated" checked=""><label for="automated">Automated</label>
            </div>
            <div class="indicator__checkbox manual">
                <input type="checkbox" id="manual" checked=""><label for="manual">Manual</label>
            </div>
        </div>

        <div class="sc__analysis__data__box syllable__tracker__wrapper">
            <div class="main__table__head__group">
                <div class="table__head__item">
                    <label>Vowels</label>
                    <span>as in...</span>
                </div>
                <div class="table__head__group">
                    <div class="head__item" v-for="(vowel, vIndex) in vowels" :key="`head-${vIndex}`"
                        @click="selectedVowel(vIndex + 1, vowel.symbol, true) || selectedVowel(vIndex + 1, vowel.short, true) ? syllableTrackerDetail('', vowel) : null">
                        <span class="head__title">{{ vowel.symbol }}</span>
                        <label :for="`vowel-${vIndex}`" :class="{ clicked: selectedVowel(vIndex + 1, vowel.short) }"
                            :style="selectedVowel(vIndex + 1, vowel.symbol, true) || selectedVowel(vIndex + 1, vowel.short, true) ? { background: 'rgb(114 92 255)', color: '#fff' } : (selectedVowel(vIndex + 1, vowel.short) ? { background: 'rgb(54, 194, 122)', color: '#fff' } : {})">{{
                                vowel.short }}
                            <input type="checkbox" :id="`vowel-${vIndex}`" style="display: none;"
                                :disabled="selectedVowel(vIndex + 1, vowel.symbol, true) || selectedVowel(vIndex + 1, vowel.short, true)"
                                :checked="selectedVowel(vIndex + 1, vowel.short)"
                                @change="toggleManualSelection($event, vIndex + 1, vowel.short)" />
                        </label>
                        <span>{{ vowel.example }}</span>
                    </div>
                </div>
            </div>

            <div class="main__checkbox__table__box">
                <!-- Define consonant rows here -->
                <div class="inner__table__cover__box">
                    <!-- You can repeat similar structure for all other consonants -->
                    <div class="checkbox__row__box" v-for="(row, cIndex) in consonantRows" :key="row.label">
                        <div class="row__lable">{{ row.label }} {{ row.label == 'y' ? '(you)' : '' }}</div>
                        <div class="checkbox__row__group">
                            <div class="checkbox__box" v-for="(vowel, vIndex) in vowels" :key="`${row.code}-${vIndex}`"
                                :class="{ purple__checked: automated(row.label, vowel.symbol) || automated(row.label, vowel.short) }"
                                @click="automated(row.label, vowel.symbol) || automated(row.label, vowel.short) ? syllableTrackerDetail(row.label, vowel) : null">
                                <input type="checkbox" :id="`${row.code}-${vIndex}`"
                                    :checked="automated(row.label, vowel.symbol) || automated(row.label, vowel.short) || manual(getIndexNo(cIndex, vIndex), row.label, vowel.short)"
                                    :disabled="automated(row.label, vowel.symbol) || automated(row.label, vowel.short)"
                                    @change="toggleManualSelection($event, getIndexNo(cIndex, vIndex), vowel.short, row.label)"
                                    :style="automated(row.label, vowel.symbol) || automated(row.label, vowel.short) ? 'pointer-events:none;' : ''" />
                                <label :for="`${row.code}-${vIndex}`"></label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <SyllableTrackerDetailModal v-if="showsyllableDetail" @close="showsyllableDetail = false"
        :data="syllableTrackerData" :assessmentId="assessmentId" :organizationId="organizationId"
        :learnerId="learnerId" />
</template>
<script setup>
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { showDialog, showToast } from '@/utils/swal';
import { onMounted, ref } from 'vue';
import WorkpacketPdf from './WorkpacketPdf.vue';
import { handleError } from '@/utils/handleError';
import { authrizedPermissions } from '@/utils/permissions';
import SyllableTrackerDetailModal from '../SyllableTrackerDetailModal.vue';

const props = defineProps({
    organizationId: Number,
    learnerId: Number,
    assessmentId: Number,
    canConductAssessment: Boolean,
});

const workpacket = ref(null);

const syllableTrackerData = ref(null);
const showsyllableDetail = ref(false);

const loader = useLoaderStore();

const vowels = [
    { symbol: "i", short: "ee", example: "see" },
    { symbol: "ɪ", short: "ih", example: "itch" },
    { symbol: "e", short: "ay", example: "say" },
    { symbol: "ɛ", short: "eh", example: "bed" },
    { symbol: "æ", short: "(none)", example: "cat" },
    { symbol: "a", short: "ah", example: "top" },
    { symbol: "ʌ or ə", short: "uh", example: "bug\n awake" },
    { symbol: "ʊ", short: "(none)", example: "foot" },
    { symbol: "ɔ", short: "aw", example: "law" },
    { symbol: "o", short: "oh", example: "go" },
    { symbol: "u", short: "oo", example: "boo" },
    { symbol: "au", short: "ow", example: "pow" },
    { symbol: "aɪ", short: "ai", example: "eye" },
    { symbol: "oɪ", short: "oi", example: "boy" },
];

const consonantRows = [
    { label: "w or wh", code: "wh" },
    { label: "h", code: "h" },
    { label: "p or b", code: "pb" },
    { label: "t or d", code: "td" },
    { label: "k or g", code: "kg" },
    { label: "m", code: "m" },
    { label: "n", code: "n" },
    { label: "ng or ŋ", code: "ngŋ" },
    { label: "f or v", code: "fv" },
    { label: "l", code: "l" },
    { label: "y or j", code: "yj" },
];

const fetchAssessment = async () => {
    try {
        loader.show();
        const { data } = await api.get(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/workpacket`);

        workpacket.value = data?.data || null;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const getIndexNo = (cIndex, vIndex) => (cIndex * vowels.length + vIndex + 1);

const selectedVowel = (indexNo, vowel, automate = false) => {
    if (automate) {
        const syllableTracker = workpacket.value?.syllableTracker;
        if (!syllableTracker) return false;
        const vowels = vowel.split(/or/i).map(v => v.trim());
        return vowels.some(v => syllableTracker[v] === 1);
    } else {
        const manualTracker = workpacket.value?.manualSyllableTrackerVowel?.[indexNo];
        if (manualTracker) {
            const manualTrackerJson = JSON.parse(manualTracker);
            return manualTrackerJson?.[vowel] == 1
        }

        return false;
    }
}

const automated = (consonant, vowel) => {
    // If not loaded yet → always false
    if (!workpacket.value || !workpacket.value?.syllableTracker) {
        return false;
    }

    const consonants = consonant.split(/or/i).map(s => s.trim().toLowerCase());
    const vowels = vowel.split(/or/i).map(s => s.trim().toLowerCase());

    const matches = [];

    consonants.forEach(c => {
        vowels.forEach(v => {
            const key = `${c}${v}`;
            if (workpacket.value?.syllableTracker?.[key] === 1) {
                matches.push(key);
            }
        });
    });

    return matches.length > 0 ?? false;
}

const manual = (indexNo, consonant, vowel) => {
    const manualTracker = workpacket.value?.manualSyllableTracker?.[indexNo]

    if (manualTracker) {
        const manualTrackerJson = JSON.parse(manualTracker);

        const consonants = consonant.split(/or/i).map(s => s.trim().toLowerCase());
        const matches = consonants.map(c => `${c}_${vowel}`).filter(key => {
            const keys = key.split('_');
            return manualTrackerJson?.[keys[0]] == 1 && manualTrackerJson?.[keys[1]] == 1;
        });

        return matches.length > 0 ?? false;
    }

    return false;
}

const toggleManualSelection = async (event, indexNo, vowel, consonant = null) => {
    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = workpacket.value?.assessment?.learner;
        if (learner?.is_administrator) {
            message = `You don’t have permission perform this action because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission perform this action because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });

        event.target.checked = false;
        return;
    }

    loader.show();
    try {
        const payload = {
            indexNo
        }

        if (consonant) {
            const consonants = consonant.split(/or/i).map(s => s.trim().toLowerCase());
            payload.consonant_item = JSON.stringify({
                [consonants[0]]: event.target.checked ? '1' : '0',
                [vowel]: event.target.checked ? '1' : '0',
            });
        } else {
            payload.vowel_item = JSON.stringify({
                [vowel]: event.target.checked ? '1' : '0',
            });
        }

        await api.post(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/manual-syllable-tracker`, payload);

        showToast({
            title: 'Success',
            icon: 'success',
            text: 'Manual syllable tracker saved successfully!'
        });

        await fetchAssessment();
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const syllableTrackerDetail = (consonant, vowel) => {
    const consonants = consonant.split(/or/i).map(s => s.trim().toLowerCase());
    const vowels = [
        ...vowel.symbol.split(/or/i),
        ...vowel.short.split(/or/i)
    ].map(s => s.trim().toLowerCase());

    const syllableTrackerDetail = workpacket.value?.syllableTrackerDetail || {};
    const matches = {};

    consonants.forEach(c => {
        vowels.forEach(v => {
            const combos = [`${c}${v}`, `${v}${c}`];

            combos.forEach(key => {
                const data = syllableTrackerDetail[key];

                if (data && typeof data === "object" && !Array.isArray(data)) {
                    Object.entries(data).forEach(([wordKey, wordObj]) => {
                        if (wordObj && typeof wordObj === "object") {
                            matches[wordKey] = wordObj;
                        }
                    });
                }
            });
        });
    });

    syllableTrackerData.value = matches;
    showsyllableDetail.value = true;
}

onMounted(async () => {
    await fetchAssessment();
})
</script>